import { h, Component } from 'preact'
import axios, { AxiosError, AxiosResponse } from 'axios'

import Header from '../../control-components/header/index'
import InputGroup from '../../control-components/ui-components/InputGroup'
import ButtonControl from '../button-control'
import Credentials from './Credentials'
import AlertComponent from '../../control-components/ui-components/AlertComponent'
import List from './List'
import globals from '../../utils/globals'
import { Section } from '../../control-components/ui-components/Section'
import Table from '../table/Table'
import Icon from '../assets'

interface CustomerClient {
  id: string
  name: string
  clientId: string
  createdAt: string
  secret: string
}

type Props = Readonly<{
  url: string
  apiBaseUrl: string
  clients: CustomerClient[]
}>

interface State {
  name: string
  processing: boolean
  clients: CustomerClient[]
  credentials?: { id: string, secret: string }
  error?: string
  validationError?: string
}

class Form extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = { name: '', processing: false, error: undefined, clients: props.clients }

    this.handleSubmit = this.handleSubmit.bind(this)
    this.handleChange = this.handleChange.bind(this)
    this.successResponse = this.successResponse.bind(this)
    this.errorResponse = this.errorResponse.bind(this)
  }

  handleChange (evt: ChangeEvent) {
    const { value } = evt.target
    this.setState({ name: value })
  }

  handleSubmit (evt: Event) {
    evt.preventDefault()
    this.setState({ validationError: undefined })

    const { processing, name } = this.state
    if (processing) {
      return
    }

    if (name === '') {
      this.setState({
        validationError: 'Please fill in the required field.'
      })
      return
    }

    this.setState({ processing: true, error: undefined })

    axios.post('/settings/api/clients', { name: name }).then(this.successResponse).catch(this.errorResponse)
  }

  handleRevoke (client: CustomerClient) {
    this.setState({ error: undefined })
    axios.post('/settings/api/revoke', { client_id: client.id }).then(() => {
      this.setState({ clients: this.state.clients.filter((curr) => curr !== client) })
    }).catch(this.errorResponse)
  }

  successResponse (response: AxiosResponse<CustomerClient>) {
    if (!response.data) {
      return
    }

    const client = response.data
    const clients = this.state.clients
    clients.push(client)

    this.setState({ processing: false, credentials: { id: client.clientId, secret: client.secret }, clients: clients })
  }

  errorResponse (error: AxiosError<{message: string}>) {
    let message = 'Something went wrong'
    const response = error.response

    if (response?.data.message) {
      message = response.data.message
    }

    this.setState({ processing: false, error: message })
  }

  getModel () {
    return [
      { header: 'Your region', accessor: 'region', size: 'xs' },
      { header: 'Your API base url', accessor: 'apiBaseUrl', size: 'm' }
    ]
  }

  getRows () {
    return [{
      region: globals('region'),
      apiBaseUrl: this.props.apiBaseUrl
    }]
  }

  handleClick () {
    if (this.state.validationError === undefined) {
      return
    }
    this.setState({ validationError: undefined })
  }

  render ({ url }: Props, { name, processing, error, credentials, clients, validationError }: State) {
    const customButtons = [
      // eslint-disable-next-line react/jsx-key
      <ButtonControl
        newStyle
        size='medium'
        title={<div>API Documentation <Icon icon='externalLink' width={16} height={16} customClass='external-link-icon' /></div>}
        onClick={() => window.open(url, '_blank')}
      />]
    return (
      <section className='oauth2-create-client'>
        <Header title='API' customButtons={customButtons} disableCancelButton disableSaveButton />
        <AlertComponent messageType={error ? 'error' : null} message={error} />
        <form onSubmit={this.handleSubmit}>
          <Section title='OAuth2'>
            <InputGroup
              newStyle
              title='Label *'
              meta='Use a label as a reminder of why you generated this client.'
              placeholder='Enter an application name'
              disabled={processing || !!credentials}
              onChange={this.handleChange}
              onClick={() => this.handleClick()}
              value={name}
              error={validationError}
              showIconBeforeErrorMessage
            />
            {!credentials && <ButtonControl variant='brand' newStyle size='medium' type='submit' title='Create an OAuth2 client' isLoading={processing} />}
            {credentials && <Credentials credentials={credentials} />}
          </Section>
        </form>
        <div className='api-access-information'>
          <h4 className='header-title'>API access information</h4>
          <Table rows={this.getRows()} model={this.getModel()} />
        </div>
        <List clients={clients} onRevokeButtonClick={(e: CustomerClient) => this.handleRevoke(e)} />
      </section>
    )
  }
}

export default Form
